import { useEffect, useRef } from "react";

/**
 * Subtle canvas-based hero backdrop:
 * — slow drifting embers + a heavy horizon line.
 * Not three.js, but delivers the "subtle 3D moment" cheaply.
 */
export default function EmberField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let W = 0, H = 0, dpr = Math.min(2, window.devicePixelRatio || 1);
    const particles = [];

    function resize() {
      const rect = canvas.getBoundingClientRect();
      W = rect.width; H = rect.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function init() {
      particles.length = 0;
      const count = Math.floor((W * H) / 22000);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r: Math.random() * 1.4 + 0.3,
          s: Math.random() * 0.25 + 0.05,
          o: Math.random() * 0.5 + 0.2,
          hue: 30 + Math.random() * 20,
        });
      }
    }

    function tick() {
      ctx.clearRect(0, 0, W, H);
      // horizon glow
      const grad = ctx.createRadialGradient(W * 0.7, H * 0.9, 20, W * 0.7, H * 0.9, Math.max(W, H) * 0.7);
      grad.addColorStop(0, "rgba(180,83,9,0.22)");
      grad.addColorStop(0.4, "rgba(180,83,9,0.06)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      for (const p of particles) {
        p.y -= p.s;
        p.x += Math.sin((p.y + p.r) * 0.01) * 0.15;
        if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 92%, 55%, ${p.o})`;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    }

    resize(); init(); tick();
    const onResize = () => { resize(); init(); };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
